import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Star, TrendingUp, Calculator, 
  Bitcoin, Coins, Receipt, PiggyBank, BarChart3,
  QrCode, FileJson, Key, Code2, Search
} from 'lucide-react';
import { motion } from 'framer-motion';

// Top Crypto Calculators
const cryptoTools = [
  {
    slug: 'crypto-profit-calculator',
    name: 'Crypto Profit Calculator',
    description: 'Calculate profits for any cryptocurrency investment instantly.',
    icon: Calculator,
    color: 'from-orange-500/20 to-amber-500/20',
    iconColor: 'text-orange-400',
    badge: 'Most Popular'
  },
  {
    slug: 'bitcoin-profit-calculator',
    name: 'Bitcoin Profit Calculator',
    description: 'Calculate BTC investment returns with real-time data.',
    icon: Bitcoin,
    color: 'from-amber-500/20 to-yellow-500/20',
    iconColor: 'text-amber-400',
    badge: 'Trending'
  },
  {
    slug: 'ethereum-profit-calculator',
    name: 'Ethereum Profit Calculator',
    description: 'Calculate ETH profits including gas fee estimates.',
    icon: Coins,
    color: 'from-blue-500/20 to-indigo-500/20',
    iconColor: 'text-blue-400'
  },
  {
    slug: 'crypto-tax-calculator',
    name: 'Crypto Tax Calculator',
    description: 'Estimate capital gains tax on cryptocurrency trades.',
    icon: Receipt,
    color: 'from-red-500/20 to-rose-500/20',
    iconColor: 'text-red-400',
    badge: 'Tax Season'
  },
  {
    slug: 'crypto-roi-calculator',
    name: 'Crypto ROI Calculator',
    description: 'Calculate return on investment for any crypto.',
    icon: TrendingUp,
    color: 'from-green-500/20 to-emerald-500/20',
    iconColor: 'text-green-400'
  },
  {
    slug: 'crypto-investment-calculator',
    name: 'Crypto Investment Calculator',
    description: 'Project future value of crypto investments.',
    icon: PiggyBank,
    color: 'from-purple-500/20 to-violet-500/20',
    iconColor: 'text-purple-400'
  }
];

// Altcoin Calculators
const altcoinTools = [
  { slug: 'solana-profit-calculator', name: 'Solana Calculator', icon: '◎' },
  { slug: 'cardano-profit-calculator', name: 'Cardano Calculator', icon: '₳' },
  { slug: 'dogecoin-profit-calculator', name: 'Dogecoin Calculator', icon: 'Ð' },
  { slug: 'polygon-profit-calculator', name: 'Polygon Calculator', icon: 'Ⓜ' },
  { slug: 'xrp-profit-calculator', name: 'XRP Calculator', icon: '✕' },
  { slug: 'litecoin-profit-calculator', name: 'Litecoin Calculator', icon: 'Ł' }
];

// Developer Tools
const developerTools = [
  {
    slug: 'json-formatter',
    name: 'JSON Formatter',
    description: 'Format, beautify, and validate JSON data.',
    icon: FileJson,
    color: 'from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400'
  },
  {
    slug: 'qr-code-generator',
    name: 'QR Code Generator',
    description: 'Generate QR codes for URLs, text, and more.',
    icon: QrCode,
    color: 'from-violet-500/20 to-purple-500/20',
    iconColor: 'text-violet-400'
  },
  {
    slug: 'password-generator',
    name: 'Password Generator',
    description: 'Create secure random passwords instantly.',
    icon: Key,
    color: 'from-emerald-500/20 to-green-500/20',
    iconColor: 'text-emerald-400'
  },
  {
    slug: 'base64-encoder',
    name: 'Base64 Encoder/Decoder',
    description: 'Encode and decode Base64 strings.',
    icon: Code2,
    color: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400'
  },
  {
    slug: 'slug-generator',
    name: 'URL Slug Generator',
    description: 'Create SEO-friendly URL slugs.',
    icon: Search,
    color: 'from-teal-500/20 to-cyan-500/20',
    iconColor: 'text-teal-400'
  }
];

const TopToolsPage = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Top Free Online Tools 2026",
    "description": "The most popular free online tools including crypto calculators, developer utilities, and more.",
    "url": "https://toolsforgeapp.com/top-tools",
    "numberOfItems": cryptoTools.length + developerTools.length,
    "itemListElement": [
      ...cryptoTools.map((tool, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "SoftwareApplication",
          "name": tool.name,
          "url": `https://toolsforgeapp.com/tools/${tool.slug}`,
          "applicationCategory": "FinanceApplication"
        }
      })),
      ...developerTools.map((tool, index) => ({
        "@type": "ListItem",
        "position": cryptoTools.length + index + 1,
        "item": {
          "@type": "SoftwareApplication",
          "name": tool.name,
          "url": `https://toolsforgeapp.com/tools/${tool.slug}`,
          "applicationCategory": "DeveloperApplication"
        }
      }))
    ]
  };

  return (
    <>
      <SEOHead
        title="Top Free Online Tools (2026) - Crypto Calculators & Dev Utilities | ToolsForge"
        description="Discover the most popular free online tools for 2026. Bitcoin profit calculator, Ethereum calculator, JSON formatter, password generator & 180+ more. No signup required."
        keywords="best free online tools 2026, crypto profit calculator, bitcoin calculator, json formatter, password generator, free developer tools"
        canonical="https://toolsforgeapp.com/top-tools"
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <div className="min-h-screen py-20 px-6" data-testid="top-tools-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <span className="text-foreground">Top Tools</span>
          </nav>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-amber-500/30 text-amber-400">
              <Star className="w-3 h-3 mr-2" />
              Updated for 2026
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Top Free Online Tools
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our most popular tools used by millions. Crypto calculators, developer utilities, 
              and productivity tools—all free, no signup required.
            </p>
          </motion.div>

          {/* Crypto Calculators Section */}
          <section className="mb-16" data-testid="crypto-tools-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold font-['Plus_Jakarta_Sans']">
                  Crypto Calculators
                </h2>
                <p className="text-muted-foreground">Calculate profits, taxes, and ROI for any cryptocurrency</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/crypto-calculators">
                  View All 15 <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cryptoTools.map((tool, index) => (
                <motion.div
                  key={tool.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/tools/${tool.slug}`}
                    className="group flex flex-col h-full p-5 rounded-xl border border-border bg-card hover:border-primary/50 transition-all"
                    data-testid={`top-tool-${tool.slug}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center`}>
                        <tool.icon className={`w-6 h-6 ${tool.iconColor}`} />
                      </div>
                      {tool.badge && (
                        <Badge variant="secondary" className="text-xs">{tool.badge}</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                      {tool.name}
                    </h3>
                    <p className="text-sm text-muted-foreground flex-1">
                      {tool.description}
                    </p>
                    <div className="flex items-center text-primary text-sm mt-3">
                      Use Free Tool <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Altcoin Quick Links */}
          <section className="mb-16 p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold mb-4 font-['Plus_Jakarta_Sans']">
              Altcoin Profit Calculators
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {altcoinTools.map((tool) => (
                <Link
                  key={tool.slug}
                  to={`/tools/${tool.slug}`}
                  className="flex items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/50 bg-card transition-all group"
                >
                  <span className="text-xl">{tool.icon}</span>
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">{tool.name.replace(' Calculator', '')}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* Developer Tools Section */}
          <section className="mb-16" data-testid="dev-tools-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold font-['Plus_Jakarta_Sans']">
                  Developer Tools
                </h2>
                <p className="text-muted-foreground">Essential utilities for developers and designers</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/category/developer-tools">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {developerTools.map((tool, index) => (
                <motion.div
                  key={tool.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/tools/${tool.slug}`}
                    className="group flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-all"
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0`}>
                      <tool.icon className={`w-5 h-5 ${tool.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold group-hover:text-primary transition-colors">
                        {tool.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {tool.description}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Stats Section */}
          <section className="grid md:grid-cols-4 gap-4 mb-16">
            {[
              { label: 'Total Tools', value: '182+', icon: BarChart3 },
              { label: 'Crypto Calculators', value: '15', icon: Calculator },
              { label: 'Blog Articles', value: '49+', icon: FileJson },
              { label: 'Always Free', value: '100%', icon: Star }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-xl border border-border bg-card text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </section>

          {/* CTA Section */}
          <div className="p-8 rounded-2xl border border-border bg-gradient-to-b from-primary/5 to-accent/5 text-center">
            <h2 className="text-2xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">
              Explore All 182+ Tools
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              From crypto calculators to developer utilities, find the perfect tool for your needs. 
              All free, no signup required.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" asChild>
                <Link to="/all-tools">
                  Browse All Tools <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/latest-updates">
                  Latest Updates
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopToolsPage;
